import { 
  BasePaymentOption, 
  ProgramTypePricing, 
  ModuleConfiguration,
  PricingValidationResult,
  validatePaymentOptions,
  validateProgramTypePricing,
  validateModuleConfiguration
} from '@/types/pricing';
import { DeliveryMode } from '@/types/program';

export interface PricingOperationResult {
  success: boolean;
  data?: any;
  errors: string[];
  warnings: string[];
  rollbackData?: any;
}

export interface PricingState {
  typeSpecificDataByType: Record<DeliveryMode, ProgramTypePricing>;
  currentProgramType: DeliveryMode;
  moduleConfiguration?: ModuleConfiguration;
  lastSavedState?: any;
}

export class PricingService {
  private state: PricingState;
  private operationHistory: Array<{
    operation: string;
    timestamp: Date;
    previousState: any;
    newState: any;
  }> = [];

  constructor(initialState: PricingState) {
    this.state = initialState;
    this.saveState('Initial state');
  }

  /**
   * Update payment options for a specific program type with validation and rollback
   */
  updatePaymentOptions(
    programType: DeliveryMode,
    paymentOptions: BasePaymentOption[],
    userId?: string
  ): PricingOperationResult {
    try {
      // Save current state for potential rollback
      const previousState = this.cloneState();
      
      // Validate payment options
      const validation = validatePaymentOptions(paymentOptions);
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          warnings: validation.warnings,
          rollbackData: previousState
        };
      }

      // Create new pricing data
      const newPricing: ProgramTypePricing = {
        ...this.state.typeSpecificDataByType[programType],
        paymentOptions,
        isConfigured: true,
        lastModified: new Date(),
        modifiedBy: userId
      };

      // Validate the complete pricing configuration
      const pricingValidation = validateProgramTypePricing(newPricing);
      if (!pricingValidation.isValid) {
        return {
          success: false,
          errors: pricingValidation.errors,
          warnings: pricingValidation.warnings,
          rollbackData: previousState
        };
      }

      // Update state
      const newState = {
        ...this.state,
        typeSpecificDataByType: {
          ...this.state.typeSpecificDataByType,
          [programType]: newPricing
        }
      };

      // Apply changes
      this.state = newState;
      this.saveState(`Updated payment options for ${programType}`);

      return {
        success: true,
        data: newPricing,
        errors: [],
        warnings: pricingValidation.warnings,
        rollbackData: previousState
      };

    } catch (error) {
      console.error('Error updating payment options:', error);
      return {
        success: false,
        errors: [`Failed to update payment options: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
        rollbackData: this.state
      };
    }
  }

  /**
   * Update module configuration with validation and rollback
   */
  updateModuleConfiguration(
    config: ModuleConfiguration,

  ): PricingOperationResult {
    try {
      // Save current state for potential rollback
      const previousState = this.cloneState();

      // Validate module configuration
      const validation = validateModuleConfiguration(config);
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          warnings: validation.warnings,
          rollbackData: previousState
        };
      }

      // Update state
      const newState = {
        ...this.state,
        moduleConfiguration: config
      };

      // Apply changes
      this.state = newState;
      this.saveState('Updated module configuration');

      return {
        success: true,
        data: config,
        errors: [],
        warnings: validation.warnings,
        rollbackData: previousState
      };

    } catch (error) {
      console.error('Error updating module configuration:', error);
      return {
        success: false,
        errors: [`Failed to update module configuration: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
        rollbackData: this.state
      };
    }
  }

  /**
   * Rollback to a previous state
   */
  rollback(rollbackData: any): PricingOperationResult {
    try {
      if (!rollbackData) {
        return {
          success: false,
          errors: ['No rollback data provided'],
          warnings: [],
        };
      }

      // Save current state before rollback
      const currentState = this.cloneState();
      
      // Apply rollback
      this.state = rollbackData;
      this.saveState('Rollback applied');

      return {
        success: true,
        data: rollbackData,
        errors: [],
        warnings: ['Rollback completed successfully'],
        rollbackData: currentState // Allow rolling back the rollback
      };

    } catch (error) {
      console.error('Error during rollback:', error);
      return {
        success: false,
        errors: [`Rollback failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
      };
    }
  }

  /**
   * Get pricing data for a specific program type
   */
  getPricingForType(programType: DeliveryMode): ProgramTypePricing | null {
    return this.state.typeSpecificDataByType[programType] || null;
  }

  /**
   * Get all pricing data
   */
  getAllPricing(): Record<DeliveryMode, ProgramTypePricing> {
    return { ...this.state.typeSpecificDataByType };
  }

  /**
   * Get module configuration
   */
  getModuleConfiguration(): ModuleConfiguration | undefined {
    return this.state.moduleConfiguration;
  }

  /**
   * Check if a program type has configured pricing
   */
  isConfigured(programType: DeliveryMode): boolean {
    const pricing = this.state.typeSpecificDataByType[programType];
    return pricing?.isConfigured || false;
  }

  /**
   * Get operation history for audit purposes
   */
  getOperationHistory() {
    return [...this.operationHistory];
  }

  /**
   * Validate all pricing data
   */
  validateAllPricing(): PricingValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate each program type pricing
    Object.entries(this.state.typeSpecificDataByType).forEach(([type, pricing]) => {
      const validation = validateProgramTypePricing(pricing);
      errors.push(...validation.errors.map(error => `${type}: ${error}`));
      warnings.push(...validation.warnings.map(warning => `${type}: ${warning}`));
    });

    // Validate module configuration if present
    if (this.state.moduleConfiguration) {
      const moduleValidation = validateModuleConfiguration(this.state.moduleConfiguration);
      errors.push(...moduleValidation.errors.map(error => `Module: ${error}`));
      warnings.push(...moduleValidation.warnings.map(warning => `Module: ${warning}`));
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Export current state for persistence
   */
  exportState(): PricingState {
    return this.cloneState();
  }

  /**
   * Import state from external source
   */
  importState(newState: PricingState): PricingOperationResult {
    try {
      // Save current state for potential rollback
      const previousState = this.cloneState();

      // Validate the new state
      const validation = this.validateAllPricing();
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          warnings: validation.warnings,
          rollbackData: previousState
        };
      }

      // Apply new state
      this.state = newState;
      this.saveState('State imported from external source');

      return {
        success: true,
        data: newState,
        errors: [],
        warnings: validation.warnings,
        rollbackData: previousState
      };

    } catch (error) {
      console.error('Error importing state:', error);
      return {
        success: false,
        errors: [`Failed to import state: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
        rollbackData: this.state
      };
    }
  }

  private saveState(operation: string) {
    this.operationHistory.push({
      operation,
      timestamp: new Date(),
      previousState: this.operationHistory.length > 0 ? this.operationHistory[this.operationHistory.length - 1].newState : null,
      newState: this.cloneState()
    });

    // Keep only last 50 operations to prevent memory issues
    if (this.operationHistory.length > 50) {
      this.operationHistory.shift();
    }
  }

  private cloneState(): PricingState {
    return JSON.parse(JSON.stringify(this.state));
  }
}

// Factory function to create pricing service
export const createPricingService = (initialState: PricingState): PricingService => {
  return new PricingService(initialState);
};
